import { useCallback, useEffect, useRef, useState } from "react";
import type {
	AssistantLogModality,
	AssistantLogStatus,
	AssistantLogUsersPageDto,
} from "@/types/ai-assistant-log";
import type { UsageLogVendor } from "@/types/ai-assistant-usage-log";
import { fetchLogUsers } from "./log.api";

/** Pilihan rentang di filter bar (hari kalender WITA). */
export const ASSISTANT_LOG_RANGE_OPTIONS = [1, 7, 30, 90] as const;
const DEFAULT_RANGE_DAYS = 30;
const PAGE_SIZE = 25;
/** Jeda sebelum kata kunci dikirim, supaya tiap ketikan tidak jadi satu request. */
const SEARCH_DEBOUNCE_MS = 350;

export interface AssistantLogFilters {
	days: number;
	vendor?: UsageLogVendor;
	status?: AssistantLogStatus;
	modality?: AssistantLogModality;
}

export interface AssistantLogState {
	/** null = belum pernah selesai dimuat (memuat pertama atau gagal). */
	page: AssistantLogUsersPageDto | null;
	loading: boolean;
	error: string | null;
	filters: AssistantLogFilters;
	setFilters: (next: AssistantLogFilters) => void;
	/** Kata kunci apa adanya di kotak cari (belum di-debounce). */
	search: string;
	setSearch: (next: string) => void;
	offset: number;
	pageSize: number;
	goToOffset: (next: number) => void;
	reload: () => void;
}

const DEFAULT_FILTERS: AssistantLogFilters = { days: DEFAULT_RANGE_DAYS };

/**
 * Lapis 1: muat halaman agregat per pengguna tiap kali filter, kata kunci
 * (setelah debounce), halaman, atau `reloadKey` berubah. Request sebelumnya
 * selalu dibatalkan supaya balasan lama tidak menimpa yang baru.
 */
export function useAssistantLog(reloadKey: unknown): AssistantLogState {
	const [filters, setFiltersState] =
		useState<AssistantLogFilters>(DEFAULT_FILTERS);
	const [search, setSearchState] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [offset, setOffset] = useState(0);
	const [page, setPage] = useState<AssistantLogUsersPageDto | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [manualReload, setManualReload] = useState(0);
	const abortRef = useRef<AbortController | null>(null);

	useEffect(() => {
		const timer = setTimeout(
			() => setDebouncedSearch(search),
			SEARCH_DEBOUNCE_MS,
		);
		return () => clearTimeout(timer);
	}, [search]);

	// Filter atau kata kunci berubah → kembali ke halaman pertama.
	const setFilters = useCallback((next: AssistantLogFilters) => {
		setFiltersState(next);
		setOffset(0);
	}, []);
	const setSearch = useCallback((next: string) => {
		setSearchState(next);
		setOffset(0);
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: reloadKey & manualReload sengaja memicu muat ulang
	useEffect(() => {
		abortRef.current?.abort();
		const controller = new AbortController();
		abortRef.current = controller;
		setLoading(true);
		setError(null);
		fetchLogUsers(
			{
				...filters,
				q: debouncedSearch.trim() || undefined,
				limit: PAGE_SIZE,
				offset,
			},
			controller.signal,
		)
			.then((result) => {
				setPage(result);
				setLoading(false);
			})
			.catch((err: unknown) => {
				if (err instanceof Error && err.name === "AbortError") return;
				setPage(null);
				setError(
					err instanceof Error ? err.message : "Gagal memuat log percakapan",
				);
				setLoading(false);
			});
		return () => controller.abort();
	}, [filters, debouncedSearch, offset, reloadKey, manualReload]);

	return {
		page,
		loading,
		error,
		filters,
		setFilters,
		search,
		setSearch,
		offset,
		pageSize: PAGE_SIZE,
		goToOffset: setOffset,
		reload: useCallback(() => setManualReload((n) => n + 1), []),
	};
}
